import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import '../styles/SubscriptionScreen.css';
import {
  createOutlineKey,
  createVlessKey,
  createObhodKey,
  checkTrial,
  activateOutlinePromo,
  activateVlessPromo,
  activateObhodPromo,
} from '../api';

function SubscriptionScreen({
  selectedVPN,
  prices,
  selectedMonths,
  setSelectedMonths,
}) {
  const navigate = useNavigate();
  const price = prices[selectedVPN.id][selectedMonths];

  const [telegramId, setTelegramId] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  // Триал
  const [trialAvailable, setTrialAvailable] = useState(false);
  const [checkingTrial, setCheckingTrial] = useState(true);

  // Промокод
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [isActivating, setIsActivating] = useState(false);

  // Блокировка кнопки "Попробовать бесплатно" во время запроса
  const [isCreatingTrial, setIsCreatingTrial] = useState(false);

  // Определяем имя таблицы для триала
  const getTableName = () => {
    if (selectedVPN.name === 'Outline') return 'dbaza';
    if (selectedVPN.name === 'Базовый') return 'vless';
    if (selectedVPN.name === 'Обход глушилок') return 'obhod';
    return null;
  };

  // Выбор функции активации промокода
  const getPromoActivator = () => {
    if (selectedVPN.name === 'Outline') return activateOutlinePromo;
    if (selectedVPN.name === 'Базовый') return activateVlessPromo;
    if (selectedVPN.name === 'Обход глушилок') return activateObhodPromo;
    return null;
  };

  // Обработчик триала
  const handleTrial = async () => {
    if (isCreatingTrial) {
     return; // уже выполняется → игнорируем повторный клик
    }

    setIsCreatingTrial(true);
    
    if (!telegramId) {
      alert('Не удалось определить ваш Telegram ID');
      setIsCreatingTrial(false);
      return;
    }

    const tableName = getTableName();
    if (!tableName) {
      alert('Неизвестный тип VPN');
      setIsCreatingTrial(false);
      return;
    }

    try {
      let response;
      if (tableName === 'dbaza') {
        response = await createOutlineKey(telegramId, telegramId, 7);
      } else if (tableName === 'vless') {
        response = await createVlessKey(telegramId, telegramId, 7);
      } else if (tableName === 'obhod') {
        response = await createObhodKey(telegramId, telegramId, 3);
      }

      if (response) {
        const days = selectedVPN.name === 'Обход глушилок' ? 3 : 7;
        alert(`Триал-ключ на ${days} дней успешно создан!`);
        navigate('/management', { state: { key: response.key } });
      } else {
        alert('Ошибка при создании триал-ключа');
      }
    } catch (err) {
      console.error(err);
      alert('Произошла ошибка при создании триал-ключа');
    } finally {
      setTimeout(() => {
        setIsCreatingTrial(false);
      }, 3500);
    }
  };

  // Обработчик активации промокода
  // Обработчик активации промокода
  const handleApplyPromo = async () => {
    if (!telegramId) {
      alert('Не удалось определить ваш Telegram ID');
      return;
    }

    if (!promoCode.trim()) {
      setPromoError('Введите промокод');
      return;
    }

    const activator = getPromoActivator();
    if (!activator) {
      setPromoError('Неизвестный тип VPN');
      return;
    }

    setIsActivating(true);
    setPromoError('');
    setPromoSuccess('');

    try {
      const result = await activator(telegramId, promoCode.trim());

      console.log('Результат активации промокода:', result);  

      if (result.success) {
        const stopDate = /^\d{2}\.\d{2}\.\d{4}$/.test(result.stop)
          ? result.stop
          : new Date(result.stop).toLocaleDateString('ru-RU');

        setPromoSuccess(`Промокод успешно активирован!\nДоступ до ${stopDate}`);
        setPromoCode('');

        // Автоматический переход на /management через 3 секунды
        setTimeout(() => {
          navigate('/management', { 
            state: { 
              // Если бэкенд возвращает ключ — передаём его, иначе просто редиректим
              key: result.key || null 
            } 
          });
        }, 3000);
      }
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Неверный или уже использованный промокод';
      setPromoError(msg);
    } finally {
      setIsActivating(false);
    }
  };

  // Получение Telegram ID
  useEffect(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      setIsIOS(window.Telegram.WebApp.platform === 'ios');

      const user = window.Telegram.WebApp.initDataUnsafe?.user;
      if (user?.id) {
        setTelegramId(user.id);
      }
    }

    // Для разработки
    if (process.env.NODE_ENV === 'development') {
      const params = new URLSearchParams(window.location.search);
      const tgIdFromUrl = params.get('tg_id');
      if (tgIdFromUrl) {
        setTelegramId(Number(tgIdFromUrl));
      } else {
        setTelegramId(328036431); // тестовый ID
      }
    }
  }, []);

  // Проверка доступности триала
  useEffect(() => {
    if (!telegramId || !selectedVPN?.name) {
      setCheckingTrial(false);
      return;
    }

    const tableName = getTableName();
    if (!tableName) {
      setCheckingTrial(false);
      return;
    }

    const fetchTrial = async () => {
      try {
        const result = await checkTrial(telegramId, tableName);
        setTrialAvailable(!!result?.trial);
      } catch (err) {
        console.error('Ошибка проверки триала:', err);
        setTrialAvailable(false);
      } finally {
        setCheckingTrial(false);
      }
    };

    fetchTrial();
  }, [telegramId, selectedVPN]);

  return (
    <div className="sub-wrapper">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="sub-content"
      >
        {/* Кнопка назад */}
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>

        {/* Заголовок */}
        <h1 className="sub-title">{selectedVPN.name}</h1>
        <p className="sub-desc">{selectedVPN.description}</p>

        {/* Переключатель периода */}
        <div className="period-toggle">
          <motion.button
            className={`period-btn ${selectedMonths === '1' ? 'active' : ''}`}
            onClick={() => setSelectedMonths('1')}
            whileTap={{ scale: 0.95 }}
          >
            1 месяц
          </motion.button>
          <motion.button
            className={`period-btn ${selectedMonths === '3' ? 'active' : ''}`}
            onClick={() => setSelectedMonths('3')}
            whileTap={{ scale: 0.95 }}
            layout
          >
            3 месяца
            {/* <span className="badge">–20%</span> */}
          </motion.button>
        </div>

        {/* Цена (без скидки от промокода) */}
        <div className="price-section">
          <div className="price-main">
            <span className="price-new">{price} ₽</span>
          </div>
        </div>

        {/* Промокод — одноразовая активация */}
        {/* <div className="promo-section">
          <input
            className="promo-input"
            placeholder="Введите промокод"
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value.toUpperCase());
              setPromoError('');
              setPromoSuccess('');
            }}
            disabled={isActivating}
          />
          <motion.button
            className="apply-promo-btn"
            whileTap={{ scale: 0.95 }}
            onClick={handleApplyPromo}
            disabled={isActivating}
          >
            {isActivating ? 'Проверка...' : 'Активировать'}
          </motion.button>

          {promoError && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="promo-error"
              style={{ color: '#ff4444', marginTop: '8px', fontSize: '14px' }}
            >
              {promoError}
            </motion.p>
          )}

          {promoSuccess && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="promo-success"
              style={{ marginTop: '12px', fontSize: '15px', textAlign: 'center' }}
            >
              {promoSuccess}
            </motion.p>
          )}
        </div> */}

        {/* Кнопка триала */}
        {!checkingTrial && trialAvailable && (
          <motion.button
            className="trial-button"
            whileTap={{ scale: 0.95 }}
            onClick={handleTrial}
            disabled={isCreatingTrial || checkingTrial || !trialAvailable}
            style={{ marginBottom: '16px' }}
          >
            {isCreatingTrial
              ? 'Создаём ключ...'
              : `Попробовать бесплатно ${selectedVPN.name === 'Обход глушилок' ? '3 дня' : '7 дней'}`}
            </motion.button>
        )}

        {/* Кнопка оплаты */}
        <motion.button
          className="pay-button"
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/payment')}
        >
          Перейти к оплате
        </motion.button>
      </motion.div>
    </div>
  );
}

export default SubscriptionScreen;